import type { QueryClient } from '@tanstack/react-query';

/**
 * Shared client cache dependency map for one organization-graph mutation.
 * These are prefixes: option/as-of/version suffixes remain covered by TanStack's
 * default partial-key matching. Cross-folder consumers are refreshed from the
 * durable graph event, not by guessing their folder IDs in this browser.
 */
export function orgPlanningDependentQueryKeys(folderId: string): readonly (readonly unknown[])[] {
  return [
    // Atlas model, review lifecycle, position/people artifacts and projections.
    ['atlas', 'org-model', folderId],
    ['atlas', 'lenses', folderId],
    ['atlas', 'position-summary', folderId],
    ['atlas', 'position-roster', folderId],
    ['atlas', 'position-roster-focus', folderId],
    ['atlas', 'staffing-people', folderId],
    ['atlas', 'position-path', folderId],
    ['atlas', 'position-assertion-history', folderId],
    ['atlas', 'organization-plans', folderId],
    ['atlas', 'organization-plan-projection', folderId],
    ['atlas', 'organization-plan-review', folderId],
    ['atlas-succession', folderId],
    ['folder-records', folderId],
    ['folder-stats', folderId],
    ['folder', folderId, 'data'],
    // Process responsibility/repository and permissioned federated joins.
    ['compass', 'process-repository', folderId],
    ['compass', 'process-impact', folderId],
    ['compass-process-impact', folderId],
    ['process-doc-review', 'compass-folder-projections', folderId],
    ['federated-records', folderId],
    // Blueprint ownership/posture projections.
    ['network-review-baseline', folderId],
    ['network-review-workflow', folderId],
    // Compass graph/federation and every derived audit surface scoped by folder.
    ['compass', 'graph', folderId],
    ['compass', 'folder-activity', folderId],
    ['compass', 'folder', folderId],
    ['compass-view', folderId],
    ['compass-federations', folderId],
    ['compass-audit-findings', folderId],
    ['compass-audit-status', folderId],
    ['compass-audit-runs', folderId],
    ['compass-audit-practice-rollup', folderId],
    ['compass-audit-completeness', folderId],
    ['compass-audit-attachments', folderId],
    ['compass-wa-pillar-report', folderId],
    ['compass', 'baselines', folderId],
  ] as const;
}

/**
 * One client-side invalidation fan-out for facts changed by the shared Atlas
 * organization-planning transaction. Protected grant changes use the stronger
 * folder-wide purge in useFolderDataGrants.
 */
export async function invalidateOrgPlanningQueries(
  queryClient: QueryClient,
  folderId: string,
): Promise<void> {
  const blockingRefetches: Promise<unknown>[] = [];
  for (const queryKey of orgPlanningDependentQueryKeys(folderId)) {
    const refetchType = queryKey[0] === 'atlas' && queryKey[1] === 'organization-plan-review'
      ? 'none' : 'active';
    const refetch = queryClient.invalidateQueries({
      queryKey,
      // The review being transitioned is obsolete as soon as Apply/Discard
      // succeeds. Refetching it while the mutation waits keeps the review
      // mounted and can deadlock draft clearing behind a stale lifecycle
      // request. Mark it stale; clearing the draft unmounts it immediately.
      // Every mounted dependent pane must converge. 'active' leaves hidden
      // dashboards stale without fetching them; nonblocking reads run in the
      // background rather than holding up the committed transition.
      refetchType,
    });
    if (blocksPlanningTransition(queryKey)) blockingRefetches.push(refetch);
    // Every dependent projection is invalidated immediately, but Apply only
    // waits for the canonical model, scenario lifecycle, and active succession
    // projection needed by the next command. Slow analytics/summary
    // projections continue in the background and cannot keep an
    // already-committed transition mounted indefinitely.
    else void refetch;
  }
  // Access-summary keys put the principal/session boundary before the target,
  // so invalidate the exact folder by predicate rather than inventing a broad
  // global prefix. The next render remains fail-closed while it refreshes.
  void queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[0] === 'atlas'
      && query.queryKey[1] === 'access'
      && query.queryKey[4] === folderId,
    refetchType: 'active',
  });
  await Promise.all(blockingRefetches);
}

function blocksPlanningTransition(queryKey: readonly unknown[]): boolean {
  return queryKey[0] === 'atlas-succession'
    || (queryKey[0] === 'atlas'
      && (queryKey[1] === 'org-model' || queryKey[1] === 'organization-plans'));
}
